<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <div>
                    <h1 class="text-left text-h5 font-weight-bold">Your Circle</h1>
                    <p class="text-body-2 text-medium-emphasis text-left mb-0">Everyone who shares your trackers</p>
                </div>
                <v-btn v-if="canManage" color="primary" prepend-icon="mdi-account-plus"
                       @click="inviteDialogSwitch = true">Invite
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="joinCode && !authStore.isMinor">
            <v-col cols="12" class="px-5">
                <v-card class="circle-join-card" rounded="lg" flat>
                    <div class="d-flex flex-wrap align-center justify-space-between pa-4 ga-3">
                        <div class="text-left">
                            <div class="d-flex align-center ga-1 mb-1">
                                <span class="text-h6 font-weight-bold text-white">{{ circleName }}</span>
                                <v-btn v-if="isOwner" icon="mdi-pencil" size="x-small" variant="text" color="white"
                                       title="Rename circle" @click="openRename"/>
                            </div>
                            <div class="text-caption text-white" style="opacity: .85">Family circle join code</div>
                            <div class="text-h5 font-weight-bold text-white" style="letter-spacing: 4px">{{ formattedCode }}</div>
                            <div class="text-caption text-white" style="opacity: .85">Share this code so family can join. It rotates every couple of days for safety.</div>
                        </div>
                        <div class="d-flex ga-2">
                            <v-btn variant="flat" color="white" size="small" prepend-icon="mdi-content-copy"
                                   @click="copyCode">Copy
                            </v-btn>
                            <v-btn variant="outlined" color="white" size="small" prepend-icon="mdi-share-variant"
                                   @click="shareCode">Share
                            </v-btn>
                            <v-btn variant="outlined" color="white" size="small" prepend-icon="mdi-qrcode"
                                   @click="qrDialog = true">QR code
                            </v-btn>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-tabs v-model="tab" color="primary" grow>
                    <v-tab value="members">
                        Members
                        <v-badge v-if="users.length" :content="users.length" color="primary" class="ml-2" inline/>
                    </v-tab>
                    <v-tab v-if="canManage" value="pending-invites">
                        Pending Invites
                        <v-badge v-if="invitedUsers.length" :content="invitedUsers.length" color="warning" class="ml-2"
                                 inline/>
                    </v-tab>
                    <v-tab v-if="canManage" value="join-requests">
                        Join Requests
                        <v-badge v-if="joinRequests.length" :content="joinRequests.length" color="error" class="ml-2"
                                 inline/>
                    </v-tab>
                    <v-tab v-if="myInvites.length" value="invitations">
                        Invitations
                        <v-badge :content="myInvites.length" color="primary" class="ml-2" inline/>
                    </v-tab>
                </v-tabs>

                <v-card variant="outlined" rounded="lg" class="mt-1"
                        style="border-top-left-radius: 0 !important; border-top-right-radius: 0 !important;">
                    <v-window v-model="tab">
                        <v-window-item value="members">
                            <v-table hover>
                                <thead>
                                <tr>
                                    <th style="width: 50px"></th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th class="text-center" style="width: 100px">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                <template v-if="loading">
                                    <tr>
                                        <td colspan="5" class="text-center pa-8">
                                            <v-progress-circular color="primary" indeterminate size="32"/>
                                            <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Loading members...</p>
                                        </td>
                                    </tr>
                                </template>
                                <template v-else-if="users.length">
                                    <tr v-for="user in users" :key="user.id" class="text-left">
                                        <td class="pr-0">
                                            <UserAvatar :image="user.avatar"
                                                        :name="`${user.first_name ?? ''} ${user.last_name ?? ''}`"
                                                        :size="32"/>
                                        </td>
                                        <td>
                                            <div>
                                                <span class="font-weight-medium">{{ user.first_name }} {{ user.last_name }}</span>
                                                <v-chip v-if="user.id === authUser.company?.owner_id" size="x-small"
                                                        color="primary" variant="tonal" class="ml-2">Owner
                                                </v-chip>
                                            </div>
                                        </td>
                                        <td class="text-medium-emphasis">{{ user.email }}</td>
                                        <td>
                                            <v-chip size="small" variant="tonal" :color="roleColor(user)">
                                                {{ roleLabel(user) }}
                                            </v-chip>
                                        </td>
                                        <td class="text-center">
                                            <!-- Owner/admins manage other members. The owner row is never actionable. -->
                                            <template v-if="canManage && user.id !== authUser.company?.owner_id">
                                                <v-btn v-if="!isMemberAdmin(user) && !user.is_minor"
                                                       icon="mdi-shield-account-outline" size="small" variant="text"
                                                       color="primary" title="Make admin"
                                                       :loading="roleBusyId === user.id"
                                                       @click="setMemberAdmin(user, true)"/>
                                                <v-btn v-else-if="isMemberAdmin(user)"
                                                       icon="mdi-shield-off-outline" size="small" variant="text"
                                                       color="warning" title="Remove admin"
                                                       :loading="roleBusyId === user.id"
                                                       @click="setMemberAdmin(user, false)"/>
                                                <v-btn icon="mdi-account-remove-outline" size="small" variant="text"
                                                       color="error" title="Remove member"
                                                       @click="deleteUser(user)"/>
                                            </template>
                                        </td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <tr>
                                        <td class="text-center pa-10" colspan="5">
                                            <v-icon size="48" color="grey-lighten-1" class="mb-3">
                                                mdi-account-group-outline
                                            </v-icon>
                                            <p class="text-body-1 text-medium-emphasis mb-1">No members yet</p>
                                            <p class="text-body-2 text-medium-emphasis mb-3">
                                                Invite people to share access to your trackers
                                            </p>
                                            <v-btn color="primary" prepend-icon="mdi-account-plus" size="small"
                                                   @click="inviteDialogSwitch = true">
                                                Invite Member
                                            </v-btn>
                                        </td>
                                    </tr>
                                </template>
                                </tbody>
                            </v-table>

                            <div v-if="totalPages > 1" class="d-flex justify-center">
                                <v-pagination v-model="currentPage" :length="totalPages"
                                              class="my-4" density="compact"
                                              @update:model-value="fetchUsers"/>
                            </div>
                        </v-window-item>

                        <v-window-item value="pending-invites">
                            <v-table hover>
                                <thead>
                                <tr>
                                    <th style="width: 50px"></th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th class="text-center" style="width: 100px">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                <template v-if="loading">
                                    <tr>
                                        <td colspan="5" class="text-center pa-8">
                                            <v-progress-circular color="primary" indeterminate size="32"/>
                                            <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Loading
                                                invitations...</p>
                                        </td>
                                    </tr>
                                </template>
                                <template v-else-if="invitedUsers.length">
                                    <tr v-for="invitedUser in invitedUsers" :key="invitedUser.id" class="text-left">
                                        <td class="pr-0">
                                            <v-avatar color="warning" size="32" variant="tonal">
                                                <v-icon size="18">mdi-email-fast-outline</v-icon>
                                            </v-avatar>
                                        </td>
                                        <td>
                                            <span class="font-weight-medium">{{ invitedUser.first_name }} {{ invitedUser.last_name }}</span>
                                        </td>
                                        <td class="text-medium-emphasis">{{ invitedUser.email }}</td>
                                        <td>
                                            <v-chip size="small" color="warning" variant="tonal">
                                                <v-icon start size="14">mdi-clock-outline</v-icon>
                                                Pending
                                            </v-chip>
                                        </td>
                                        <td class="text-center">
                                            <v-btn icon="mdi-email-sync-outline" size="small" variant="text"
                                                   color="primary" title="Resend invitation"
                                                   :loading="resendingId === invitedUser.id"
                                                   @click="resendInvitation(invitedUser)"/>
                                            <v-btn icon="mdi-close-circle-outline" size="small" variant="text"
                                                   color="error" title="Cancel invitation"
                                                   @click="deleteInvitation(invitedUser)"/>
                                        </td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <tr>
                                        <td class="text-center pa-10" colspan="5">
                                            <v-icon size="48" color="grey-lighten-1" class="mb-3">
                                                mdi-email-send-outline
                                            </v-icon>
                                            <p class="text-body-1 text-medium-emphasis mb-1">No pending invitations</p>
                                            <p class="text-body-2 text-medium-emphasis mb-3">
                                                Send an invite to share tracker access with someone
                                            </p>
                                            <v-btn color="primary" prepend-icon="mdi-account-plus" size="small"
                                                   @click="inviteDialogSwitch = true">
                                                Invite Someone
                                            </v-btn>
                                        </td>
                                    </tr>
                                </template>
                                </tbody>
                            </v-table>

                            <div v-if="tab2TotalPages > 1" class="d-flex justify-center">
                                <v-pagination v-model="tab2CurrentPage" :length="tab2TotalPages"
                                              class="my-4" density="compact"
                                              @update:model-value="fetchInvitedUsers"/>
                            </div>
                        </v-window-item>

                        <v-window-item value="join-requests">
                            <v-table hover>
                                <thead>
                                <tr>
                                    <th style="width: 50px"></th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th class="text-center" style="width: 180px">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                <template v-if="requestsLoading">
                                    <tr>
                                        <td colspan="4" class="text-center pa-8">
                                            <v-progress-circular color="primary" indeterminate size="32"/>
                                            <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Loading requests...</p>
                                        </td>
                                    </tr>
                                </template>
                                <template v-else-if="joinRequests.length">
                                    <tr v-for="req in joinRequests" :key="req.id" class="text-left">
                                        <td class="pr-0">
                                            <UserAvatar :image="req.user?.avatar"
                                                        :name="`${req.user?.first_name ?? ''} ${req.user?.last_name ?? ''}`"
                                                        :size="32"/>
                                        </td>
                                        <td class="font-weight-medium">
                                            {{ req.user?.first_name }} {{ req.user?.last_name }}
                                        </td>
                                        <td class="text-medium-emphasis">{{ req.user?.email }}</td>
                                        <td class="text-center">
                                            <v-btn size="small" color="success" variant="tonal" class="me-2"
                                                   :loading="respondingId === req.id"
                                                   @click="respondRequest(req, true)">Approve
                                            </v-btn>
                                            <v-btn size="small" color="error" variant="text"
                                                   :loading="respondingId === req.id"
                                                   @click="respondRequest(req, false)">Decline
                                            </v-btn>
                                        </td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <tr>
                                        <td class="text-center pa-10" colspan="4">
                                            <v-icon size="48" color="grey-lighten-1" class="mb-3">
                                                mdi-account-clock-outline
                                            </v-icon>
                                            <p class="text-body-1 text-medium-emphasis mb-1">No join requests</p>
                                            <p class="text-body-2 text-medium-emphasis mb-0">
                                                When someone enters your circle code, their request shows up here
                                            </p>
                                        </td>
                                    </tr>
                                </template>
                                </tbody>
                            </v-table>
                        </v-window-item>

                        <v-window-item value="invitations">
                            <v-list v-if="myInvites.length" lines="two">
                                <v-list-item v-for="inv in myInvites" :key="inv.id">
                                    <template #prepend>
                                        <v-icon color="primary" class="me-2">mdi-account-multiple-plus</v-icon>
                                    </template>
                                    <v-list-item-title class="font-weight-medium">{{ inv.company_name }}</v-list-item-title>
                                    <v-list-item-subtitle>Invited by {{ inv.invited_by || 'a circle owner' }}</v-list-item-subtitle>
                                    <template #append>
                                        <v-btn size="small" color="success" variant="tonal" class="me-2"
                                               :loading="respondingInvite === inv.id" @click="acceptInvite(inv)">Accept</v-btn>
                                        <v-btn size="small" color="error" variant="text"
                                               :loading="respondingInvite === inv.id" @click="declineInvite(inv)">Decline</v-btn>
                                    </template>
                                </v-list-item>
                            </v-list>
                            <div v-else class="text-center pa-10">
                                <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-email-outline</v-icon>
                                <p class="text-body-1 text-medium-emphasis mb-0">No invitations</p>
                            </div>
                        </v-window-item>
                    </v-window>
                </v-card>
            </v-col>
        </v-row>
    </v-container>

    <Invite v-if="inviteDialogSwitch" v-model="inviteDialogSwitch" @update-invited-user="updateUser" @refresh-invites="fetchInvitedUsers"/>

    <v-dialog v-model="renameDialog" max-width="400">
        <v-card rounded="lg" class="pa-2">
            <v-card-title class="text-h6">Rename circle</v-card-title>
            <v-card-text>
                <v-text-field v-model="renameValue" label="Circle name" maxlength="50" counter
                              variant="outlined" density="comfortable" autofocus
                              @keyup.enter="saveRename"/>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
                <v-spacer/>
                <v-btn variant="text" @click="renameDialog = false">Cancel</v-btn>
                <v-btn color="primary" variant="flat" :loading="renaming"
                       :disabled="!renameValue?.trim()" @click="saveRename">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="qrDialog" max-width="340">
        <v-card rounded="lg" class="text-center pa-5">
            <h3 class="text-h6 font-weight-bold mb-1">Join my family circle</h3>
            <p class="text-body-2 text-medium-emphasis mb-4">Scan this in the SimpliGPS Go app to join.</p>
            <div class="d-flex justify-center mb-4">
                <qrcode-vue v-if="joinCode" :value="joinCode" :size="200" level="M"/>
            </div>
            <div class="text-h6 font-weight-bold" style="letter-spacing: 4px">{{ formattedCode }}</div>
            <div class="d-flex justify-center ga-2 mt-4">
                <v-btn variant="tonal" color="primary" prepend-icon="mdi-content-copy" @click="copyCode">Copy code</v-btn>
                <v-btn variant="text" @click="qrDialog = false">Close</v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, onMounted, onBeforeUnmount, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useNavigation} from "@/composables/globals";
import router from "@/router";
import Invite from "@/components/dialogs/Invite.vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useClipboard} from "@/composables/clipboard";
import QrcodeVue from "qrcode.vue";
import UserAvatar from "@/components/UserAvatar.vue";

const {navigateTo} = useNavigation();
const authStore = useAuthStore();
const loading = ref(true);
const authUser = authStore.user;
const inviteDialogSwitch = ref(false);
const {showSnackbar} = useSnackbarStore();
const tab = ref('members');
const totalPages = ref(1);
const currentPage = ref(1);
const tab2TotalPages = ref(1);
const tab2CurrentPage = ref(1);
const invitedUsers = ref([]);
const users = ref([]);

const {copyToClipboard} = useClipboard();
const joinCode = ref(null);
const joinCodeExpires = ref(null);
const qrDialog = ref(false);
// Display the 6-char code as XXX-XXX for readability.
const formattedCode = computed(() => {
    const c = joinCode.value;
    return c && c.length >= 6 ? `${c.slice(0, 3)}-${c.slice(3, 6)}` : (c ?? '');
});
const circleName = ref(authUser.company?.company_name ?? 'My circle');
const renameDialog = ref(false);
const renameValue = ref('');
const renaming = ref(false);

const openRename = () => {
    renameValue.value = circleName.value;
    renameDialog.value = true;
};
const saveRename = () => {
    const name = renameValue.value?.trim();
    if (!name) return;
    renaming.value = true;
    send('post', 'circle/rename', {name})
        .then((response) => {
            if (response.data.success) {
                circleName.value = name;
                authStore.updateAuthCompany({...authUser.company, company_name: name});
                showSnackbar('Circle renamed');
                renameDialog.value = false;
            }
        })
        .catch((error) => showSnackbar(error.response?.data?.message ?? 'Could not rename circle', 'error'))
        .finally(() => renaming.value = false);
};
const joinRequests = ref([]);
const requestsLoading = ref(false);
const respondingId = ref(null);

// The owner or an adult admin manages the active circle; plain members and
// minors don't. Only the owner can rename/transfer the circle.
const isOwner = computed(() => authStore.user?.id === authStore.user?.company?.owner_id);
const canManage = computed(() => authStore.isCircleAdmin && !authStore.isMinor);
// Invitations sent TO this user (the in-app inbox; how a minor accepts a
// parent's invite without the email link).
const myInvites = ref([]);
const respondingInvite = ref(null);

const fetchMyInvitations = () => {
    send('get', 'circle/invitations')
        .then((r) => { myInvites.value = r.data?.data ?? []; })
        .catch(() => {});
};
const acceptInvite = (inv) => {
    respondingInvite.value = inv.id;
    send('post', `accept-invitation/${inv.token}`, {})
        .then(() => {
            showSnackbar(`Joined ${inv.company_name || 'the circle'}`);
            router.go(); // reload so membership + switcher reflect the new circle
        })
        .catch((e) => showSnackbar(e.response?.data?.message ?? 'Could not accept the invitation', 'error'))
        .finally(() => respondingInvite.value = null);
};
const declineInvite = (inv) => {
    respondingInvite.value = inv.id;
    send('post', `circle/invitations/${inv.id}/decline`)
        .then(() => {
            myInvites.value = myInvites.value.filter((i) => i.id !== inv.id);
            showSnackbar('Invitation declined');
        })
        .catch((e) => showSnackbar(e.response?.data?.message ?? 'Could not decline', 'error'))
        .finally(() => respondingInvite.value = null);
};

const copyCode = () => {
    if (!joinCode.value) return;
    copyToClipboard(formattedCode.value);
    showSnackbar('Join code copied');
};
const shareCode = async () => {
    if (!joinCode.value) return;
    const text = `Join my family circle on SimpliGPS Go. Enter code ${formattedCode.value} in the app.`;
    if (navigator.share) {
        try {
            await navigator.share({title: 'SimpliGPS Go', text});
            return;
        } catch (e) {
            // user cancelled or share unavailable; fall through to clipboard
        }
    }
    copyToClipboard(text);
    showSnackbar('Invite text copied');
};

const fetchJoinRequests = (autoTab = false) => {
    requestsLoading.value = true;
    send('get', 'circle/join-requests')
        .then((response) => {
            joinRequests.value = response.data.data ?? [];
            // Surface pending requests by opening the Join Requests tab - but ONLY
            // on initial load (autoTab), never on a live reload, or the owner gets
            // yanked off the Members tab every time a reload fires while a request
            // is pending.
            if (autoTab && joinRequests.value.length && tab.value === 'members') {
                tab.value = 'join-requests';
            }
        })
        .catch(() => {
            // Owner-only endpoint; non-owners are redirected away on mount.
        })
        .finally(() => requestsLoading.value = false);
};
const respondRequest = (req, approve) => {
    respondingId.value = req.id;
    send('post', `circle/join-requests/${req.id}/${approve ? 'approve' : 'reject'}`)
        .then((response) => {
            if (response.data.success) {
                joinRequests.value = joinRequests.value.filter((r) => r.id !== req.id);
                showSnackbar(response.data.message);
                if (approve) fetchUsers();
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? 'Something went wrong', 'error');
        })
        .finally(() => respondingId.value = null);
};

const fetchJoinCode = () => {
    send('get', 'circle/code')
        .then((response) => {
            joinCode.value = response.data?.data?.code ?? null;
            joinCodeExpires.value = response.data?.data?.expires_at ?? null;
        })
        .catch(() => {});
};

onMounted(() => {
    // Everyone in a circle can open this page; what they see is role/age-aware.
    fetchUsers();
    fetchMyInvitations();
    if (canManage.value) {
        fetchJoinCode();
        fetchInvitedUsers();
        fetchJoinRequests(true); // auto-open Join Requests tab only on first load
    } else {
        tab.value = myInvites.value.length ? 'invitations' : 'members';
    }
    window.addEventListener('company-data-reload', onCompanyDataReload);
});

onBeforeUnmount(() => {
    window.removeEventListener('company-data-reload', onCompanyDataReload);
    if (reloadDebounce) clearTimeout(reloadDebounce);
});

// A circle-data change arrived over the reload socket (a member joined/left, was
// removed, etc). DEBOUNCED (a burst of observer saves can fire several reloads):
// coalesce into one refetch, and do NOT auto-switch tabs on a reload (that would
// yank an owner off the Members tab every time a request is pending). Mirrors
// Devices.vue's debounced reload wiring.
let reloadDebounce = null;
const onCompanyDataReload = () => {
    if (reloadDebounce) clearTimeout(reloadDebounce);
    reloadDebounce = setTimeout(() => {
        fetchUsers();
        if (canManage.value) {
            fetchInvitedUsers();
            fetchJoinRequests(); // no auto-tab on a live reload
        }
    }, 800);
};

const updateUser = (newUser) => {
    if (invitedUsers.value.findIndex((user) => user.id === newUser.id) === -1) {
        invitedUsers.value.unshift(newUser)
    }
}

const fetchUsers = () => {
    // Read the company id reactively + guarded: authUser is a one-time snapshot,
    // and if the circle was concurrently removed, authUser.company is null and
    // authUser.company.id throws SYNCHRONOUSLY - before send() returns, so it
    // isn't caught by the promise chain and aborts the rest of onMounted
    // (including the reload-listener registration). Bail cleanly instead.
    const cid = authStore.user?.company?.id;
    if (!cid) return;
    loading.value = true;
    send('get', `companies/${cid}/users?page=${currentPage.value}`)
        .then((response) => {
            users.value = response.data.data.users.data;
            totalPages.value = response.data.data.users.last_page;
            currentPage.value = response.data.data.users.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Members could not be loaded', 'error')
        })
        .finally(() => loading.value = false)
}
const fetchInvitedUsers = () => {
    const cid = authStore.user?.company?.id;
    if (!cid) return;
    send('get', `invited-users`, {company: cid, page: tab2CurrentPage.value})
        .then((response) => {
            invitedUsers.value = response.data.data.users.data;
            tab2TotalPages.value = response.data.data.users.last_page;
            tab2CurrentPage.value = response.data.data.users.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Invitations could not be loaded', 'error')
        })
}
// Role display + per-member admin toggle.
const ownerId = computed(() => authUser.company?.owner_id);
const isMemberAdmin = (user) => !!user.pivot?.is_admin;
const roleLabel = (user) => user.id === ownerId.value ? 'Owner' : (isMemberAdmin(user) ? 'Admin' : 'Member');
const roleColor = (user) => user.id === ownerId.value ? 'primary' : (isMemberAdmin(user) ? 'indigo' : 'grey');
const roleBusyId = ref(null);
const setMemberAdmin = (user, makeAdmin) => {
    roleBusyId.value = user.id;
    send('post', `circle/members/${user.id}/admin`, {is_admin: makeAdmin})
        .then((response) => {
            if (user.pivot) user.pivot.is_admin = makeAdmin;
            showSnackbar(response.data?.message ?? (makeAdmin ? 'Member is now an admin' : 'Admin access removed'));
        })
        .catch((error) => showSnackbar(error.response?.data?.message ?? 'Could not update the role', 'error'))
        .finally(() => roleBusyId.value = null);
};

const deleteUser = (user) => {
    if (confirm(`Are you sure you want to remove "${user.first_name} ${user.last_name}" from your account?`)) {
        send('put', `companies/${authUser.company.id}/unassign/${user.id}`)
            .then((response) => {
                if (response.data.success) {
                    users.value.map((comp, index) => {
                        if (comp.id === user.id) {
                            users.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`Could not remove "${user.first_name}"`, 'error')
            })
    }
}
const resendingId = ref(null);
const resendInvitation = (invitation) => {
    resendingId.value = invitation.id;
    send('post', `invited-users/${invitation.id}/resend`)
        .then((response) => {
            if (response.data.success) showSnackbar(response.data.message ?? 'Invitation resent');
        })
        .catch((error) => showSnackbar(error.response?.data?.message ?? 'Could not resend the invitation', 'error'))
        .finally(() => resendingId.value = null);
};
const deleteInvitation = (invitation) => {
    if (confirm(`Are you sure you want to cancel the invitation for "${invitation.first_name} ${invitation.last_name}"?`)) {
        send('delete', `invited-users/${invitation.id}`)
            .then((response) => {
                if (response.data.success) {
                    invitedUsers.value.map((item, index) => {
                        if (item.id === invitation.id) {
                            invitedUsers.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`Could not cancel invitation for "${invitation.first_name}"`, 'error')
            })
    }
}
</script>

<style scoped>
.circle-join-card {
    background: linear-gradient(135deg, #26235A, #E6038C);
}
</style>
